import {useState, useEffect, useRef} from "react";
import {useNavigate} from "react-router-dom";
import {API_BASE_URL} from "../../config";
import "../../css/chatbot.css";
import axios from "../../api/axiosInstance";
import BotMessage from "./BotMessage";


interface MenuItem {
    name: string;
    description: string;
    ingredients: string;
    price: number;
    image_url?: string;
}

type MessageType = "user" | "bot" | "typing";

interface ChatMessage {
    type: MessageType;
    content: string;
    menuItems?: MenuItem[];
}


interface Theme {
    chat_header_gradient: string;
    user_msg_bg: string;
    bot_msg_bg: string;
    font: string;
    send_btn_gradient: string;
    toggle_bg: string;
}
interface UsageData {
    count: number;
    plan: string;
    total_chats: number;
    error_rate: number;
    busiest_hour: string;
    is_trial_active?: boolean;
    trial_ends_in_days?: number;
    trial_end_date?: string;
    is_payment_valid: boolean;
    is_demo?: boolean;
}

export default function Chatbot({restaurantId, restaurantSlug}: { restaurantId?: string; restaurantSlug?: string; }) {
    const restaurantIdentifier = restaurantSlug || restaurantId;
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [allergies, setAllergies] = useState("");
    const [cravings, setCravings] = useState("");
    const [timeOfDay, setTimeOfDay] = useState("");
    const [weather, setWeather] = useState("Fetching weather...");
    const [budget, setBudget] = useState<number | "">("");
    const [isTyping, setIsTyping] = useState(false);

    const [isOpen, setIsOpen] = useState(false);

    const [enabledFeatures, setEnabledFeatures] = useState<string[]>([]);
    const [theme, setTheme] = useState<Theme>({
        chat_header_gradient: "",
        user_msg_bg: "",
        bot_msg_bg: "",
        font: "",
        send_btn_gradient: "",
        toggle_bg: ""
    });

    const navigate = useNavigate();


    const chatBoxRef = useRef<HTMLDivElement | null>(null);


    const layout = new URLSearchParams(window.location.search).get("layout");
    const isEmbedded = layout === "compact";
    const [isPaymentValid, setIsPaymentValid] = useState(true);
    const [isDemo, setIsDemo] = useState(false); // 👈 Add this


    const requestInFlightRef = useRef(false);




    useEffect(() => {
        fetchWeather();
    }, []);

    useEffect(() => {
        if (restaurantIdentifier) {
            axios.get<{ features: string[] }>(`/subscription/features/${restaurantIdentifier}`)
                .then(res => setEnabledFeatures(res.data.features || []))
                .catch(err => console.error("Failed to fetch features:", err));

            axios.get<Theme>(`/bot/theme/${restaurantIdentifier}`)
                .then(res => setTheme(res.data))
                .catch(err => console.error("Error fetching theme:", err));
        }
    }, [restaurantIdentifier]);



    useEffect(() => {
        const chatBox = chatBoxRef.current;
        if (!chatBox) return;

        const botMessages =
            chatBox.querySelectorAll<HTMLElement>(".chat-message.bot");

        const latestBotMessage = botMessages[botMessages.length - 1];

        if (latestBotMessage) {
            chatBox.scrollTo({
                top: latestBotMessage.offsetTop - chatBox.offsetTop,
                behavior: "smooth"
            });
        }
    }, [messages]);



    useEffect(() => {
        if (isEmbedded) {
            setIsOpen(true);
        }
    }, [isEmbedded]);



    const fetchWeather = async () => {
        try {
            const res = await fetch(`${API_BASE_URL}/bot/weather-api`);
            const data = await res.json();
            setWeather(`${data.temperature}°C - ${data.condition}`);
        } catch {
            setWeather("Weather unavailable");
        }
    };

    const sendRecommendationRequest = async () => {
        // Prevent duplicate requests, including rapid double-clicks.
        if (requestInFlightRef.current) return;

        if (!restaurantIdentifier) {
            setMessages(prev => [
                ...prev,
                {
                    type: "bot",
                    content: "Restaurant information is unavailable."
                }
            ]);
            return;
        }

        if (!cravings.trim() && !budget) return;

        let userMessage = `Here are my preferences:\n`
            + `• Allergies: ${allergies || "None"}\n`
            + `• Cravings: ${cravings || "Any"}\n`
            + `• Time of Day: ${timeOfDay || "Anytime"}\n`;

        if (enabledFeatures.includes("Weather Integration")) {
            userMessage += `• Weather: ${weather || "Unknown"}\n`;
        }

        userMessage += `• Budget: ${budget ? `$${budget}` : "No limit"}`;

        // Match the backend's 500-character validation.
        if (userMessage.length > 500) {
            setMessages(prev => [
                ...prev,
                {
                    type: "bot",
                    content: "Your preferences are too long. Please shorten them."
                }
            ]);
            return;
        }

        requestInFlightRef.current = true;
        setIsTyping(true);

        setMessages(prev => [
            ...prev,
            { type: "user", content: userMessage }
        ]);

        try {
            const res = await fetch(
                `${API_BASE_URL}/bot/chatbot/${restaurantIdentifier}`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json"
                    },
                    body: JSON.stringify({
                        message: userMessage
                    })
                }
            );

            const data = await res.json();

            if (!res.ok) {
                if (res.status === 429) {
                    throw new Error(
                        "You've reached the demo request limit. Please try again later."
                    );
                }

                throw new Error(
                    data.error || `Request failed (${res.status})`
                );
            }

            if (typeof data.response !== "string" || !data.response.trim()) {
                throw new Error("No recommendation was returned.");
            }

            // Display successful responses immediately.
            setMessages(prev => [
                ...prev,
                {
                    type: "bot",
                    content: data.response,
                    menuItems: Array.isArray(data.menu_items)
                        ? data.menu_items
                        : []
                }
            ]);

        } catch (error) {
            console.error("Recommendation request failed:", error);

            const errorMessage =
                error instanceof Error
                    ? error.message
                    : "Unable to get recommendations.";

            setMessages(prev => [
                ...prev,
                {
                    type: "bot",
                    content: errorMessage
                }
            ]);

        } finally {
            requestInFlightRef.current = false;
            setIsTyping(false);
        }
    };


    useEffect(() => {
        axios.get<UsageData>(`/bot/public-usage/${restaurantIdentifier}`)
            .then(res => {
                const isDemo = res.data.is_demo || false; // 👈 Add
                const isPaymentOk = res.data.is_payment_valid || isDemo; // 👈 Add
                setIsDemo(isDemo);
                setIsPaymentValid(isPaymentOk);
            })
            .catch(err => console.error("Failed to check payment validity:", err));
    }, [restaurantIdentifier]);


    // if (!isPaymentValid) return  null; // or show error message
    // if (!isPaymentValid) {
    //     return (
    //         <div className="chatbot-container p-4 bg-white shadow rounded text-center text-sm text-red-500">
    //             ⚠️ This chatbot is temporarily unavailable. Please contact the restaurant.
    //         </div>
    //     );
    // }


    return (
        <>
        {!isEmbedded && (
            <div
                className="chat-toggle"
                onClick={() => {
                    if (!isOpen) {
                        fetchWeather();
                    }
                    setIsOpen(!isOpen);
                }}
                style={{background: theme.toggle_bg, fontFamily: theme.font}}
            >
                💬
            </div>
        )}
            {isOpen && (
                <div className={`chatbot-container ${isEmbedded ? "compact-chatbot" : ""}`}>
                    <div
                        className="chat-header"
                        onClick={() => setIsOpen(false)}
                        style={{background: theme.chat_header_gradient, fontFamily: theme.font}}
                    >
                        <span>💬 Ask for Food Recommendations</span>
                    </div>


                    <div
                        className="chat-box"
                        id="chat-bot"
                        ref={chatBoxRef}
                    >

                        {!isPaymentValid ? (
                            <div className="flex justify-center items-center h-full">
                                <div
                                    className="text-red-600 text-sm text-center border border-red-200 bg-red-50 rounded shadow-sm p-4 max-w-xs mx-auto">
                                    ⚠️ This chatbot is temporarily unavailable. <br/> Please contact the restaurant or
                                    try again later.
                                </div>
                            </div>
                        ) : (
                            <>
                                {messages.map((msg, index) => (
                                    <div
                                        key={index}
                                        className={`chat-message ${msg.type}`}
                                        style={{
                                            background: msg.type === "user" ? theme.user_msg_bg : theme.bot_msg_bg,
                                            color: "#fff",
                                            alignSelf: msg.type === "user" ? "flex-end" : "flex-start",
                                            fontFamily: theme.font,
                                        }}
                                    >
                                        {msg.type === "typing" ? (
                                            <span className="typing-indicator">Getting menu...</span>
                                        ) : msg.type === "bot" ? (
                                            <BotMessage content={msg.content} menuItems={msg.menuItems}/>
                                        ) : (
                                            <p>{msg.content}</p>
                                        )}
                                    </div>

                                ))}
                                {isTyping && <div className="typing-indicator">Getting menu...</div>}
                            </>
                        )}
                    </div>

                    <div className="chat-input">
                    <input type="text" placeholder="Any allergies?" value={allergies}
                               onChange={(e) => setAllergies(e.target.value)}
                               disabled={!isPaymentValid}/>
                        <input type="text" placeholder="What do you feel like eating?" value={cravings}
                               onChange={(e) => setCravings(e.target.value)}
                               disabled={!isPaymentValid}/>
                        <input type="text" placeholder="Breakfast, Lunch, or Dinner?" value={timeOfDay}
                               onChange={(e) => setTimeOfDay(e.target.value)}
                               disabled={!isPaymentValid}/>
                        {(enabledFeatures.includes("Weather Integration") || isDemo) && (
                            <input type="text" value={weather} readOnly/>
                        )}
                        <input type="number" placeholder="Max budget ($)" value={budget}
                               onChange={(e) => setBudget(e.target.value ? parseFloat(e.target.value) : "")}
                               disabled={!isPaymentValid}/>
                        <button
                            onClick={sendRecommendationRequest}
                            disabled={!isPaymentValid || isTyping}
                            style={{
                                background: theme.send_btn_gradient,
                                color: "#fff",
                                border: "none",
                                padding: "10px",
                                borderRadius: "6px",
                                fontWeight: "bold",
                                fontFamily: theme.font,
                                cursor: isPaymentValid ? "pointer" : "not-allowed"
                            }}
                        >

                            {isTyping ? "Finding your meal..." : "Get Recommendation"}

                        </button>
                    </div>
                </div>
            )}
        </>
    );
}
